import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { Empty, FormDialog, PageHead, money, useApi } from "@/components/shop/common";

const FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "phone", label: "Phone", half: true },
  { name: "hourly_rate", label: "Pay rate / hr", type: "number", half: true },
  { name: "skills", label: "Skills / certifications", type: "textarea" },
];

const weekHours = (entries, id) => {
  const since = Date.now() - 7 * 864e5;
  return entries.filter((e) => e.technician_id === id && e.hours && new Date(e.start).getTime() > since).reduce((a, e) => a + e.hours, 0);
};

export default function Technicians() {
  const [techs, load] = useApi("/technicians");
  const [entries, loadE] = useApi("/time");
  const [wos] = useApi("/work-orders");
  const [edit, setEdit] = useState(null);
  const save = async (v) => { if (edit.id) await api.put(`/technicians/${edit.id}`, v); else await api.post("/technicians", v); toast.success("Technician saved"); load(); };
  const del = async (t) => { if (window.confirm(`Delete ${t.name}?`)) { await api.delete(`/technicians/${t.id}`); load(); } };
  const stop = async (id) => { try { await api.post(`/time/${id}/clock-out`); toast.success("Clocked out"); loadE(); } catch (e) { toast.error(errMsg(e)); } };
  const woOf = (id) => wos?.find((w) => w.id === id);

  return (
    <>
      <PageHead eyebrow="Team" title="Technicians">
        <Button onClick={() => setEdit({ hourly_rate: 0 })} className="rounded-sm hover:bg-orange-600 hover:text-white" data-testid="add-tech-btn"><Plus className="h-4 w-4 mr-1" /> Technician</Button>
      </PageHead>
      <p className="text-sm text-muted-foreground mb-6">Clock techs in from a work order's Time clock panel. Hours roll up here.</p>
      {techs?.length === 0 ? <Empty testid="techs-empty">No technicians yet.</Empty> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {techs?.map((t) => {
            const open = (entries || []).find((e) => e.technician_id === t.id && !e.end);
            const wh = weekHours(entries || [], t.id);
            const wo = open && woOf(open.work_order_id);
            return (
              <div key={t.id} className="bg-card border border-border rounded-sm p-5" data-testid={`tech-card-${t.id}`}>
                <div className="flex justify-between">
                  <div>
                    <p className="font-display text-2xl uppercase">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{[t.phone, t.skills].filter(Boolean).join(" · ")}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEdit(t)} className="text-muted-foreground hover:text-white" data-testid={`tech-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(t)} className="text-muted-foreground hover:text-red-400" data-testid={`tech-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Hours 7d</p><p className="font-display text-3xl font-bold" data-testid={`tech-hours-${t.id}`}>{wh.toFixed(1)}</p></div>
                  <div><p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Pay 7d</p><p className="font-display text-3xl font-bold">{money(wh * t.hourly_rate)}</p></div>
                </div>
                {open ? (
                  <div className="mt-4 flex items-center justify-between border border-orange-500/40 bg-orange-500/10 rounded-sm px-3 py-2 text-sm" data-testid={`tech-clocked-${t.id}`}>
                    <Link to={`/app/work-orders/${open.work_order_id}`} className="hover:text-primary"><span className="inline-block h-2 w-2 rounded-full bg-orange-500 animate-pulse mr-2" />On WO #{wo?.number}</Link>
                    <button onClick={() => stop(open.id)} className="flex items-center gap-1 text-orange-300 hover:text-white" data-testid={`tech-clock-out-${t.id}`}><Square className="h-3.5 w-3.5" /> Stop</button>
                  </div>
                ) : <p className="mt-4 font-mono text-xs text-muted-foreground">OFF THE CLOCK</p>}
              </div>
            );
          })}
        </div>
      )}
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} title={edit?.id ? "Edit technician" : "New technician"} fields={FIELDS} initial={edit} onSubmit={save} testid="tech-dialog" />
    </>
  );
}
